/* eslint-disable react-refresh/only-export-components */
import {
  DimeAlert,
  DimeAlertAction,
  DimeAlertDescription,
  DimeAlertTitle,
  DimeButton,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircleIcon, AlertTriangleIcon, CheckCircle2Icon, InfoIcon } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/alert')({
  component: AlertDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function AlertDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Alert</h1>
      <p className="mb-8 text-muted-foreground">Displays a callout for user attention.</p>

      <DocsSection title="Basic">
        <Preview>
          <DimeAlert>
            <CheckCircle2Icon />
            <DimeAlertTitle>Payment successful</DimeAlertTitle>
            <DimeAlertDescription>
              Your payment of $29.99 has been processed. A receipt has been sent to your email
              address.
            </DimeAlertDescription>
          </DimeAlert>
          <DimeAlert>
            <InfoIcon />
            <DimeAlertTitle>New feature available</DimeAlertTitle>
            <DimeAlertDescription>
              We&apos;ve added dark mode support. You can enable it in your account settings.
            </DimeAlertDescription>
          </DimeAlert>
        </Preview>
      </DocsSection>

      <DocsSection title="Destructive">
        <Preview>
          <DimeAlert variant="destructive">
            <AlertCircleIcon />
            <DimeAlertTitle>Payment failed</DimeAlertTitle>
            <DimeAlertDescription>
              Your payment could not be processed. Please check your payment method and try again.
            </DimeAlertDescription>
          </DimeAlert>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono text-xs">variant=&quot;destructive&quot;</code> for errors
          that require immediate attention.
        </p>
      </DocsSection>

      <DocsSection title="Warning, info and success">
        <Preview>
          <div className="flex flex-col gap-3">
            <DimeAlert variant="warning">
              <AlertTriangleIcon />
              <DimeAlertTitle>Stock is running low</DimeAlertTitle>
              <DimeAlertDescription>Only 4 units remain at this location.</DimeAlertDescription>
            </DimeAlert>
            <DimeAlert variant="info">
              <DimeAlertTitle>Shipped with a remainder</DimeAlertTitle>
              <DimeAlertDescription>
                The unshipped quantity moved to a linked transfer.
              </DimeAlertDescription>
            </DimeAlert>
            <DimeAlert variant="success">
              <CheckCircle2Icon />
              <DimeAlertTitle>Everything’s picked</DimeAlertTitle>
              <DimeAlertDescription>The order has left the picking list.</DimeAlertDescription>
            </DimeAlert>
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono text-xs">variant=&quot;warning&quot;</code> for a caution,{' '}
          <code className="font-mono text-xs">variant=&quot;info&quot;</code> for neutral context
          that is not an error, and{' '}
          <code className="font-mono text-xs">variant=&quot;success&quot;</code> when a task is
          done.
        </p>
      </DocsSection>

      <DocsSection title="Action">
        <Preview>
          <DimeAlert>
            <DimeAlertTitle>Dark mode is now available</DimeAlertTitle>
            <DimeAlertDescription>
              Enable it under your profile settings to get started.
            </DimeAlertDescription>
            <DimeAlertAction>
              <DimeButton size="sm" variant="default">
                Enable
              </DimeButton>
            </DimeAlertAction>
          </DimeAlert>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono text-xs">DimeAlertAction</code> to add a button positioned
          in the top-right corner of the alert.
        </p>
      </DocsSection>

      <DocsSection title="Custom colors">
        <Preview>
          <DimeAlert className="border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-50">
            <AlertTriangleIcon />
            <DimeAlertTitle>Your subscription will expire in 3 days.</DimeAlertTitle>
            <DimeAlertDescription>
              Renew now to avoid service interruption or upgrade to a paid plan to continue using
              the service.
            </DimeAlertDescription>
          </DimeAlert>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass custom Tailwind classes to <code className="font-mono text-xs">DimeAlert</code> to
          override the background, border, and text colors.
        </p>
      </DocsSection>

      <DocsSection title="Props">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="pb-2 pr-4 font-medium">Component</th>
              <th className="pb-2 pr-4 font-medium">Prop</th>
              <th className="pb-2 pr-4 font-medium">Type</th>
              <th className="pb-2 font-medium">Default</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">DimeAlert</td>
              <td className="py-2 pr-4 font-mono text-xs">variant</td>
              <td className="py-2 pr-4 text-muted-foreground">
                <code className="font-mono text-xs">
                  &quot;default&quot; | &quot;destructive&quot; | &quot;warning&quot; |
                  &quot;info&quot; | &quot;success&quot;
                </code>
              </td>
              <td className="py-2 text-muted-foreground">
                <code className="font-mono text-xs">&quot;default&quot;</code>
              </td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">DimeAlertTitle</td>
              <td className="py-2 pr-4 font-mono text-xs">className</td>
              <td className="py-2 pr-4 text-muted-foreground">string</td>
              <td className="py-2 text-muted-foreground">—</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">DimeAlertDescription</td>
              <td className="py-2 pr-4 font-mono text-xs">className</td>
              <td className="py-2 pr-4 text-muted-foreground">string</td>
              <td className="py-2 text-muted-foreground">—</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">DimeAlertAction</td>
              <td className="py-2 pr-4 font-mono text-xs">className</td>
              <td className="py-2 pr-4 text-muted-foreground">string</td>
              <td className="py-2 text-muted-foreground">—</td>
            </tr>
          </tbody>
        </table>
      </DocsSection>
    </div>
  );
}
